import React, { useEffect, useState, useRef } from 'react';

import { Box } from '@sunwisesoftware/sunwise-ui';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';

import alerts from 'common/modules/alerts';

import * as companyGeneralActions from '../../../modules/companyGeneral/actions';
import * as profileActions from '../../../modules/profile/actions';
import * as profileSelectors from '../../../modules/profile/selectors';
import * as profileConfigurationActions from '../../../modules/profileConfiguration/actions';

import Footer from './Footer';
import SideBar from './SideBar';
import TopBar from './TopBar';

const DefaultLayout = ({
    children,
    fetchCompanyData,
    fetchUserSettings,
    match,
    profileValues,
    redirect,
    saveConfig,
    selectedTheme,
    setTheme,
}) => {
    const [drawerSize, setDrawerSize] = useState('normal');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const searchFieldRef = useRef(null);

    useEffect(() => {
        fetchUserSettings();
        fetchCompanyData();
        const selectedDrawerSize = localStorage.getItem('selectedDrawerSize');
        if (selectedDrawerSize) setDrawerSize(selectedDrawerSize);
    }, []);

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const toggleDrawerSize = () => {
        const newDrawerSize = drawerSize === 'normal' ? 'small' : 'normal';
        setDrawerSize(newDrawerSize);
        setSidebarOpen(false);
        saveConfig({ expanded_sidebar: drawerSize === 'small' });
        localStorage.setItem('selectedDrawerSize', newDrawerSize);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <SideBar
                drawerSize={drawerSize}
                handleDrawerToggle={handleDrawerToggle}
                match={match}
                mobileOpen={mobileOpen}
                profileValues={profileValues}
                redirect={redirect}
                selectedTheme={selectedTheme}
                setMobileOpen={setMobileOpen}
                setSidebarOpen={setSidebarOpen}
                setTheme={setTheme}
                sidebarOpen={sidebarOpen}
                toggleDrawerSize={toggleDrawerSize}
            />

            <Box
                component="main"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    ml: { md: '246px' },
                    pt: { xs: '52px', md: 0 },
                    width: { md: 'calc(100% - 246px)' },
                    transition: 'margin-left 0.3s ease, width 0.3s ease',
                    ...(drawerSize === 'small' && {
                        ml: { md: '66px' },
                        width: { md: 'calc(100% - 66px)' },
                    }),
                }}
            >
                <TopBar
                    handleDrawerToggle={handleDrawerToggle}
                    match={match}
                    searchFieldRef={searchFieldRef}
                    profileValues={profileValues}
                    redirect={redirect}
                    selectedTheme={selectedTheme}
                    setTheme={setTheme}
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                    }}
                >
                    {children}
                </Box>

                <Footer />
            </Box>
            <alerts.Container />
        </Box>
    );
};

const mapStateToProps = createStructuredSelector({
    profileValues: profileSelectors.getCurrentValues,
    selectedTheme: profileSelectors.getTheme,
});

const mapDispatchToProps = (dispatch) => ({
    fetchCompanyData: () => dispatch(companyGeneralActions.fetchCompanyData()),
    fetchUserSettings: () => dispatch(profileActions.fetchData()),
    redirect: (link) => dispatch(push(link)),
    saveConfig: (config) =>
        dispatch(profileConfigurationActions.saveConfig(config)),
    setTheme: (value) => dispatch(profileActions.setTheme(value)),
});

DefaultLayout.propTypes = {
    children: PropTypes.object,
    fetchCompanyData: PropTypes.func,
    fetchUserSettings: PropTypes.func,
    match: PropTypes.object,
    profileValues: PropTypes.object,
    redirect: PropTypes.func,
    saveConfig: PropTypes.func,
    selectedTheme: PropTypes.string,
    setTheme: PropTypes.func,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(DefaultLayout));
